import { batch, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import {
  IAnalysisInfoMutation,
  IAnalysisInfoMutationVariables,
} from '../apollo/graphqlSchema';
import { CHANGE_ANALYSIS_INFO } from '../apollo/analysis';
import printGraphqlErrors from '../apollo/printGraphqlErrors';
import { renameAnalysisAction } from '../../redux/slices/analyses';
import { renameAnalysisVisit } from '../../redux/slices/analysesVisits';
import { dashboardInstanceUpdateReportInfo } from '../../redux/slices/dashboards/dashboardInstance';
import { useCurrentUrl } from '../routing';

interface IUpdateAnalysisInfoArgs {
  analysisId: string;
  currentName: string;
  currentDescription?: string;
  onSubmit?: () => void;
}

export default ({
  analysisId,
  currentName,
  currentDescription,
  onSubmit,
}: IUpdateAnalysisInfoArgs) => {
  const dispatch = useDispatch();

  const { isReportInUrl, isCreateCohortInUrl } = useCurrentUrl();

  const [changeAnalysisInfo, { loading }] = useMutation<
    IAnalysisInfoMutation,
    IAnalysisInfoMutationVariables
  >(CHANGE_ANALYSIS_INFO);

  const updateAnalysisInfo = async (
    newName: string,
    newDescription?: string,
  ) => {
    const description = newDescription ?? currentDescription ?? '';

    const response = await changeAnalysisInfo({
      variables: {
        input: {
          analysisId,
          description,
          display: newName,
        },
      },
    });

    if (printGraphqlErrors(response?.data?.analysisInfo?.errors)) {
      return;
    }

    batch(() => {
      dispatch(
        renameAnalysisAction({
          analysisId,
          analysisName: newName,
          analysisDescription: description,
        }),
      );

      // When name/description is updated in the report, we need to update it in the dashboard redux
      // so that there won't be a mismatch between the dashboard and the report
      dispatch(
        dashboardInstanceUpdateReportInfo({
          analysisId,
          newName,
          newDescription: description,
        }),
      );

      if (isReportInUrl || isCreateCohortInUrl) {
        dispatch(
          renameAnalysisVisit({
            analysisId,
            analysisName: newName,
          }),
        );
      }
    });

    onSubmit?.();
  };

  const updateAnalysisDescription = async (newDescription: string) => {
    await updateAnalysisInfo(currentName, newDescription);
  };

  return {
    updateAnalysisInfo,
    updateAnalysisDescription,
    loading,
  };
};
